/**
 * Admin Teams & Approvals Page
 * Combines team management and approval workflows in a tabbed interface
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Collapse,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Skeleton,
  TablePagination,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Groups as GroupsIcon,
  CheckCircle as ActiveIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Edit as RevisionIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { adminApi, TeamAdminInfo } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import type { OnboardingRequest, ApprovalStatus, AuditLogEntry } from '@/types';

const teamStatusColors: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const approvalStatusColors: Record<ApprovalStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  revision_requested: 'info',
};

// ============================================================================
// Teams Tab Component
// ============================================================================
function TeamsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamAdminInfo | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [confirmTeamName, setConfirmTeamName] = useState('');
  const [newStatus, setNewStatus] = useState({ is_active: true, approval_status: '' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-teams', search, statusFilter, page, rowsPerPage],
    queryFn: () =>
      adminApi
        .listTeamsAdmin({
          search: search || undefined,
          approval_status: statusFilter || undefined,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        })
        .then((res) => res.data),
    placeholderData: (previousData) => previousData,
  });

  const updateTeamMutation = useMutation({
    mutationFn: ({
      teamId,
      data,
    }: {
      teamId: string;
      data: { is_active?: boolean; approval_status?: string };
    }) => adminApi.updateTeamAdmin(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teams'] });
      setEditDialogOpen(false);
      setSelectedTeam(null);
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: (teamId: string) => adminApi.deleteTeamAdmin(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teams'] });
      setDeleteDialogOpen(false);
      setSelectedTeam(null);
    },
  });

  const permanentDeleteTeamMutation = useMutation({
    mutationFn: (teamId: string) => adminApi.permanentlyDeleteTeamAdmin(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teams'] });
      setPermanentDeleteDialogOpen(false);
      setSelectedTeam(null);
      setConfirmTeamName('');
    },
  });

  const handleOpenEditDialog = (team: TeamAdminInfo) => {
    setSelectedTeam(team);
    setNewStatus({
      is_active: team.is_active,
      approval_status: team.approval_status,
    });
    setEditDialogOpen(true);
  };

  const handleSaveStatus = () => {
    if (selectedTeam) {
      updateTeamMutation.mutate({
        teamId: selectedTeam.id,
        data: newStatus,
      });
    }
  };

  const handleOpenDeleteDialog = (team: TeamAdminInfo) => {
    setSelectedTeam(team);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (selectedTeam) {
      deleteTeamMutation.mutate(selectedTeam.id);
    }
  };

  const handleOpenPermanentDeleteDialog = (team: TeamAdminInfo) => {
    setSelectedTeam(team);
    setConfirmTeamName('');
    setPermanentDeleteDialogOpen(true);
  };

  const handlePermanentDelete = () => {
    if (selectedTeam) {
      permanentDeleteTeamMutation.mutate(selectedTeam.id);
    }
  };

  const stats = {
    total: data?.total || 0,
    approved: data?.items.filter((t) => t.approval_status === 'approved').length || 0,
    pending: data?.items.filter((t) => t.approval_status === 'pending').length || 0,
    rejected: data?.items.filter((t) => t.approval_status === 'rejected').length || 0,
  };

  if (error) {
    return <Alert severity="error">Failed to load teams. Please try again.</Alert>;
  }

  return (
    <Box>
      {/* Stats */}
      <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-around', flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{stats.total}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Approved</Typography>
              <Typography variant="subtitle1" fontWeight={700} color="success.main">{stats.approved}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="subtitle1" fontWeight={700} color="warning.main">{stats.pending}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Rejected</Typography>
              <Typography variant="subtitle1" fontWeight={700} color="error.main">{stats.rejected}</Typography>
            </Box>
          </Box>
          <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
            <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)} color={filtersOpen || statusFilter !== '' ? 'primary' : 'default'}>
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Filters (collapsible) */}
      <Collapse in={filtersOpen}>
        <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                placeholder="Search teams by name or space ID..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button size="small" startIcon={<ClearIcon />} onClick={() => { setSearch(''); setStatusFilter(''); setPage(0); }} disabled={!search && !statusFilter}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Teams Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell>Space ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No teams found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.items.map((team) => (
                <TableRow key={team.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupsIcon color="action" fontSize="small" />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={500}>{team.name}</Typography>
                          <Chip label={team.member_count} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }} />
                        </Box>
                        {team.description && (
                          <Typography variant="caption" color="text.secondary">
                            {team.description.substring(0, 50)}{team.description.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                      {team.google_space_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={team.approval_status}
                      color={teamStatusColors[team.approval_status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={team.is_active ? <ActiveIcon /> : <BlockIcon />}
                      label={team.is_active ? 'Active' : 'Inactive'}
                      color={team.is_active ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(team.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit team status">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(team)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate team">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(team)}
                        disabled={!team.is_active}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Permanently delete team">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenPermanentDeleteDialog(team)}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </TableContainer>

      {/* Edit Status Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Team Status</DialogTitle>
        <DialogContent>
          {selectedTeam && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Team: <strong>{selectedTeam.name}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {selectedTeam.google_space_id}
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Approval Status</InputLabel>
                <Select
                  value={newStatus.approval_status}
                  label="Approval Status"
                  onChange={(e) => setNewStatus((prev) => ({ ...prev, approval_status: e.target.value }))}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Active Status</InputLabel>
                <Select
                  value={newStatus.is_active ? 'active' : 'inactive'}
                  label="Active Status"
                  onChange={(e) => setNewStatus((prev) => ({ ...prev, is_active: e.target.value === 'active' }))}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveStatus} variant="contained" disabled={updateTeamMutation.isPending}>
            {updateTeamMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Team</DialogTitle>
        <DialogContent>
          {selectedTeam && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will deactivate the team and set its approval status to rejected.
              </Alert>
              <Typography variant="body2">
                Are you sure you want to deactivate <strong>{selectedTeam.name}</strong>?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleteTeamMutation.isPending}>
            {deleteTeamMutation.isPending ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => {
          setPermanentDeleteDialogOpen(false);
          setConfirmTeamName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Permanently Delete Team</DialogTitle>
        <DialogContent>
          {selectedTeam && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>This action is irreversible.</strong> This will permanently delete the team
                and ALL associated data including members, configurations, analytics, cached
                responses, and chat history. The Google Space ID will become available for reuse.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Team: <strong>{selectedTeam.name}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Google Space ID: <code>{selectedTeam.google_space_id}</code>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Members: {selectedTeam.member_count}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                Type the team name <strong>{selectedTeam.name}</strong> to confirm:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={confirmTeamName}
                onChange={(e) => setConfirmTeamName(e.target.value)}
                placeholder={selectedTeam.name}
                autoFocus
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermanentDeleteDialogOpen(false);
              setConfirmTeamName('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePermanentDelete}
            variant="contained"
            color="error"
            disabled={
              permanentDeleteTeamMutation.isPending || confirmTeamName !== selectedTeam?.name
            }
          >
            {permanentDeleteTeamMutation.isPending ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Approvals Tab Component
// ============================================================================
function ApprovalsTab() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [statusTabValue, setStatusTabValue] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState<OnboardingRequest | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | 'revision' | null;
  }>({ open: false, action: null });
  const [comments, setComments] = useState('');
  const [auditDialog, setAuditDialog] = useState<{ open: boolean; requestId: string | null }>({
    open: false,
    requestId: null,
  });

  const statusFilters: (ApprovalStatus | undefined)[] = [
    undefined,
    'pending',
    'approved',
    'rejected',
    'revision_requested',
  ];

  const { data: pendingList, isLoading } = useQuery({
    queryKey: ['pending-approvals', statusFilters[statusTabValue]],
    queryFn: () =>
      adminApi.listPending({ status: statusFilters[statusTabValue] }).then((res) => res.data),
  });

  const { data: approvalStats } = useQuery({
    queryKey: ['approval-stats'],
    queryFn: () => adminApi.getStats().then((res) => res.data),
  });

  const { data: auditLog, isLoading: auditLoading } = useQuery({
    queryKey: ['audit-log', auditDialog.requestId],
    queryFn: () =>
      adminApi.getAuditLog(auditDialog.requestId!).then((res) => res.data),
    enabled: !!auditDialog.requestId,
  });

  const approveMutation = useMutation({
    mutationFn: (requestId: string) =>
      adminApi.approve(requestId, {
        action: 'approved',
        admin_email: user?.email || '',
        comments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      closeActionDialog();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (requestId: string) =>
      adminApi.reject(requestId, {
        action: 'rejected',
        admin_email: user?.email || '',
        comments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      closeActionDialog();
    },
  });

  const revisionMutation = useMutation({
    mutationFn: (requestId: string) =>
      adminApi.requestRevision(requestId, {
        action: 'revision_requested',
        admin_email: user?.email || '',
        comments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      closeActionDialog();
    },
  });

  const openActionDialog = (request: OnboardingRequest, action: 'approve' | 'reject' | 'revision') => {
    setSelectedRequest(request);
    setActionDialog({ open: true, action });
    setComments('');
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, action: null });
    setSelectedRequest(null);
    setComments('');
  };

  const handleAction = () => {
    if (!selectedRequest) return;
    switch (actionDialog.action) {
      case 'approve':
        approveMutation.mutate(selectedRequest.id);
        break;
      case 'reject':
        rejectMutation.mutate(selectedRequest.id);
        break;
      case 'revision':
        revisionMutation.mutate(selectedRequest.id);
        break;
    }
  };

  const isActionPending = approveMutation.isPending || rejectMutation.isPending || revisionMutation.isPending;

  if (isLoading) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  return (
    <Box>
      {/* Stats + Filter */}
      <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Pending</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{pendingList?.pending || 0}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Approved</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{pendingList?.approved || 0}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Rejected</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{pendingList?.rejected || 0}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Revision</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{pendingList?.revision_requested || 0}</Typography>
            </Box>
            {approvalStats && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">This Week</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{approvalStats.requests_this_week || 0}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">This Month</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{approvalStats.requests_this_month || 0}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Avg. Time</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {approvalStats.avg_approval_time_hours ? `${approvalStats.avg_approval_time_hours.toFixed(1)}h` : 'N/A'}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusTabValue}
            onChange={(_, v) => { if (v !== null) setStatusTabValue(v); }}
          >
            <ToggleButton value={0}>All</ToggleButton>
            <ToggleButton value={1}>Pending</ToggleButton>
            <ToggleButton value={2}>Approved</ToggleButton>
            <ToggleButton value={3}>Rejected</ToggleButton>
            <ToggleButton value={4}>Revision</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Approval Rate + Pending notice */}
        {approvalStats && approvalStats.total_requests > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Approval Rate
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(approvalStats.approved_count / approvalStats.total_requests) * 100}
                  color="success"
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
              <Typography variant="caption" fontWeight={600}>
                {((approvalStats.approved_count / approvalStats.total_requests) * 100).toFixed(0)}%
              </Typography>
            </Box>
          </>
        )}
      </Paper>

      {/* Requests Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Reviewed By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingList?.requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{request.team_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{request.google_space_id}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{request.requested_by_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{request.requested_by_email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={request.status} color={approvalStatusColors[request.status]} size="small" />
                </TableCell>
                <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{request.reviewed_by || '-'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View Audit Log">
                    <IconButton size="small" onClick={() => setAuditDialog({ open: true, requestId: request.id })}>
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                  {(request.status === 'pending' || request.status === 'revision_requested') && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => openActionDialog(request, 'approve')}>
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Request Revision">
                        <IconButton size="small" color="info" onClick={() => openActionDialog(request, 'revision')}>
                          <RevisionIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => openActionDialog(request, 'reject')}>
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {pendingList?.requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>No requests found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog.action === 'approve' && 'Approve Team'}
          {actionDialog.action === 'reject' && 'Reject Team'}
          {actionDialog.action === 'revision' && 'Request Revision'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Team: <strong>{selectedRequest.team_name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Requested by: {selectedRequest.requested_by_email}
              </Typography>
              {selectedRequest.notes && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Notes from requester:</strong> {selectedRequest.notes}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
          <TextField
            label={actionDialog.action === 'revision' ? 'Feedback for Team Lead (required)' : 'Comments (optional)'}
            multiline
            rows={3}
            fullWidth
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            required={actionDialog.action === 'revision'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={actionDialog.action === 'approve' ? 'success' : actionDialog.action === 'reject' ? 'error' : 'info'}
            onClick={handleAction}
            disabled={isActionPending || (actionDialog.action === 'revision' && !comments)}
          >
            {isActionPending ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialog.open} onClose={() => setAuditDialog({ open: false, requestId: null })} maxWidth="md" fullWidth>
        <DialogTitle>Audit Log</DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Performed By</TableCell>
                    <TableCell>Status Change</TableCell>
                    <TableCell>Comments</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLog?.map((entry: AuditLogEntry) => (
                    <TableRow key={entry.id}>
                      <TableCell><Chip label={entry.action} size="small" variant="outlined" /></TableCell>
                      <TableCell>{entry.performed_by}</TableCell>
                      <TableCell>
                        {entry.previous_status && (<><Chip label={entry.previous_status} size="small" /> → </>)}
                        <Chip label={entry.new_status} size="small" />
                      </TableCell>
                      <TableCell>{entry.comments || '-'}</TableCell>
                      <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialog({ open: false, requestId: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================
export default function AdminTeamsPage() {
  const [mainTab, setMainTab] = useState(0);

  return (
    <Box>
      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Teams" />
          <Tab label="Approvals" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {mainTab === 0 && <TeamsTab />}
      {mainTab === 1 && <ApprovalsTab />}
    </Box>
  );
}
